import { UzButton, UzConfirmDialog, UzDialog } from '@utazzo/ui';
import { Trash2 } from 'lucide-react';
import React, { useState } from 'react';

import { formatInrPaise } from '../invoices.helpers';
import { useChallanDetail, useRemoveInvoice } from '../use-tcs-challans';

import { TcsChallanDocuments } from './TcsChallanDocuments';
import { TcsChallanTimeline } from './TcsChallanTimeline';

import { formatDMY } from '@/lib/date-format';

interface TcsChallanPanelProps {
  challanId: string | null;
  onClose: () => void;
}

const Section = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): React.JSX.Element => (
  <section className="space-y-2">
    <h3 className="text-sm font-semibold text-zinc-900">{title}</h3>
    {children}
  </section>
);

/**
 * Full challan view: invoices (removable), the challan document and the audit
 * timeline. Paid status and the TCS certificate are per-invoice (on the register
 * row), not on the challan.
 */
export const TcsChallanPanel = ({
  challanId,
  onClose,
}: TcsChallanPanelProps): React.JSX.Element => {
  const detailQuery = useChallanDetail(challanId);
  const removeInvoice = useRemoveInvoice();
  const [removeTarget, setRemoveTarget] = useState<{ id: string; number: string } | null>(null);
  const c = detailQuery.data;

  return (
    <UzDialog
      open={challanId !== null}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={c ? `Challan ${c.challanNo ?? '(no number yet)'}` : 'Challan'}
      description={
        c
          ? `${c.invoiceCount} invoice${c.invoiceCount === 1 ? '' : 's'} - TCS ${formatInrPaise(c.totalTcsPaise)}${c.challanDate ? ` - Deposit date ${formatDMY(c.challanDate)}` : ''}`
          : undefined
      }
      size="xl"
      footer={
        <div className="flex justify-end">
          <UzButton variant="outline" onClick={onClose}>
            Close
          </UzButton>
        </div>
      }
    >
      {detailQuery.isLoading ? (
        <p className="text-sm text-zinc-500">Loading challan...</p>
      ) : detailQuery.isError || !c ? (
        <p className="text-sm text-red-600">Could not load the challan.</p>
      ) : (
        <div className="space-y-6">
          {c.note && <p className="text-sm text-zinc-500">Note: {c.note}</p>}

          <Section title="Invoices">
            {c.invoices.length === 0 ? (
              <p className="text-sm text-zinc-500">No invoices in this challan.</p>
            ) : (
              <ul className="divide-y divide-zinc-100 rounded-lg border border-zinc-200">
                {c.invoices.map((inv) => (
                  <li key={inv.invoiceId} className="flex items-center justify-between px-3 py-2">
                    <div className="text-sm text-zinc-800">
                      <span className="tabular-nums">{inv.invoiceNumber}</span>
                      <span className="ml-2 text-zinc-500">{inv.customerName ?? '-'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium tabular-nums">
                        {formatInrPaise(inv.tcsPaise)}
                      </span>
                      <UzButton
                        size="sm"
                        variant="ghost"
                        aria-label={`Remove ${inv.invoiceNumber}`}
                        onClick={() =>
                          setRemoveTarget({ id: inv.invoiceId, number: inv.invoiceNumber })
                        }
                      >
                        <Trash2 className="h-4 w-4 text-zinc-400" />
                      </UzButton>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section title="Documents">
            <TcsChallanDocuments challanId={c.id} documents={c.documents} />
          </Section>

          <Section title="Audit timeline">
            <TcsChallanTimeline events={c.events} />
          </Section>

          <UzConfirmDialog
            open={removeTarget !== null}
            onOpenChange={(o) => !o && setRemoveTarget(null)}
            title="Remove this invoice from the challan?"
            description={removeTarget ? `${removeTarget.number} will become unassigned again.` : ''}
            confirmLabel="Remove"
            tone="destructive"
            onConfirm={async () => {
              if (removeTarget) {
                await removeInvoice.mutateAsync({ id: c.id, invoiceId: removeTarget.id });
                setRemoveTarget(null);
              }
            }}
          />
        </div>
      )}
    </UzDialog>
  );
};
